import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { List, Box, Collapse, alpha, IconButton, Tooltip } from '@mui/material';
import { TuneOutlined, CheckOutlined } from '@mui/icons-material';
// hooks
import useLocales from '../../../hooks/useLocales';
import useNavPins from '../../../hooks/useNavPins';
import useAuth from '../../../hooks/useAuth';
import useNavFlyout from '../../../hooks/useNavFlyout';
// guards
import RoleBasedGuard from '../../../guards/RoleBasedGuard';
import { isItemVisible } from '../../../guards/checkNavAccess';
import { getActive } from '..';
// components
import Iconify from '../../Iconify';
//
import { ListSubheaderStyle } from './style';
import NavList from './NavList';
import NavItem from './NavItem';

// ----------------------------------------------------------------------

const COLLAPSED_SECTIONS_KEY = 'nav_collapsed_sections';

const getStoredCollapsed = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_SECTIONS_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

// Does this nav item (or any descendant) match the current pathname?
const itemContainsPath = (item, pathname) => {
  if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) return true;
  if (item.children) return item.children.some((child) => itemContainsPath(child, pathname));
  return false;
};

// ----------------------------------------------------------------------

NavSectionVertical.propTypes = {
  isCollapse: PropTypes.bool,
  navConfig: PropTypes.array,
};

export default function NavSectionVertical({ navConfig, isCollapse, ...other }) {
  const { translate } = useLocales();
  const { pinnedPaths } = useNavPins();
  const { pathname } = useLocation();
  const { user, hasPermission } = useAuth();
  const { onEnterFlyout, activeFlyoutPath } = useNavFlyout();

  // Recursive visibility: hides items whose sub-items are all gated (e.g. the
  // "Commerce" parent when no sub-item is allowed) and, in turn, sections that
  // would render with no visible items.
  const canSee = (item) => isItemVisible(item, { user, hasPermission });
  const [editMode, setEditMode] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(getStoredCollapsed);

  const toggleSection = (subheader) => {
    setCollapsedSections((prev) => {
      const next = prev.includes(subheader)
        ? prev.filter((s) => s !== subheader)
        : [...prev, subheader];
      localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Hide sections that have no items this user can see.
  const visibleGroups = navConfig.filter((group) => group.items.some(canSee));

  // Collect all root-level nav items in a flat list so we can look them up by path
  const allRootItems = navConfig.flatMap((group) => group.items);

  // Find pinned items that exist in the current navConfig (respects role filtering)
  const pinnedItems = pinnedPaths
    .map((path) => allRootItems.find((item) => item.path === path))
    .filter(Boolean);

  // A module marked `flyout` shows as ONE row here and opens its own panel
  // beside the nav. Its children never unfold inline, which is the whole
  // point: eleven of them would bury everything under it.
  //
  // When the flyout panel is not available (a phone, or icon-collapse mode
  // where there is no room for a second panel) the module falls back to the
  // ordinary expanding NavList, so nothing becomes unreachable.
  const renderItem = (item) => {
    const useFlyout = item.flyout && !!onEnterFlyout;
    if (!useFlyout) {
      return (
        <NavList
          key={item.title + item.path}
          data={item}
          depth={1}
          hasChildren={!!item.children}
          isCollapse={isCollapse}
          editMode={editMode}
        />
      );
    }
    return (
      <NavItem
        key={item.title + item.path}
        item={{
          ...item,
          children: undefined,
          info: (
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              sx={{ width: 16, height: 16, color: 'text.disabled' }}
            />
          ),
        }}
        depth={1}
        isCollapse={isCollapse}
        active={activeFlyoutPath === item.path || getActive(item.path, pathname)}
        component={RouterLink}
        to={item.path}
        onClick={() => onEnterFlyout(item.path)}
      />
    );
  };

  return (
    <Box {...other}>
      {/* ── Pinned section ─────────────────────────────────────────────── */}
      {pinnedItems.length > 0 && (
        <List disablePadding sx={{ pl: 0.75, pr: 1 }}>
          <ListSubheaderStyle
            sx={{
              ...(isCollapse && {
                opacity: 0,
                height: 12,
                minHeight: 12,
                py: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }),
            }}
          >
            {translate('Pinned')}
          </ListSubheaderStyle>

          {pinnedItems.map((item) => (
            <RoleBasedGuard
              key={`pinned-${item.title}${item.path}`}
              isNav
              allowed={item.allowed}
              permission={item.permission}
              permissions={item.permissions}
              slug={item.slug}
              roles={item.roles}
            >
              <Box
                sx={(theme) => ({
                  borderRadius: 1,
                  ...(isCollapse
                    ? {}
                    : {
                        background: alpha(theme.palette.primary.main, 0.04),
                        borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        mb: 0.25,
                      }),
                })}
              >
                {renderItem(item)}
              </Box>
            </RoleBasedGuard>
          ))}
        </List>
      )}

      {/* ── Normal groups (empty sections hidden) ──────────────────────── */}
      {visibleGroups.map((group, groupIndex) => {
        // Section containing the active route is always shown; icon-collapse mode shows all.
        const hasActive = group.items.some((item) => itemContainsPath(item, pathname));
        const isOpen = isCollapse || hasActive || !collapsedSections.includes(group.subheader);
        const isCustomizeRow = groupIndex === 0 && !isCollapse;

        return (
          <List key={group.subheader} disablePadding sx={{ pl: 0.75, pr: 1 }}>
            <ListSubheaderStyle
              onClick={() => !isCollapse && toggleSection(group.subheader)}
              sx={{
                ...(isCollapse && {
                  opacity: 0,
                  height: 12,
                  minHeight: 12,
                  py: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }),
                display: 'flex',
                alignItems: 'center',
                pr: 0.5,
                ...(!isCollapse && {
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: (theme) => theme.transitions.create('color'),
                  '&:hover': { color: 'text.secondary' },
                }),
              }}
            >
              {!isCollapse && (
                <Iconify
                  icon="eva:arrow-ios-downward-fill"
                  sx={{
                    width: 14,
                    height: 14,
                    mr: 0.5,
                    flexShrink: 0,
                    transition: (theme) => theme.transitions.create('transform'),
                    ...(!isOpen && { transform: 'rotate(-90deg)' }),
                  }}
                />
              )}

              <Box component="span" sx={{ flex: 1 }}>{translate(group.subheader)}</Box>

              {isCustomizeRow && (
                <Tooltip title={editMode ? 'Done' : 'Customize'} placement="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditMode((v) => !v);
                    }}
                    sx={{
                      p: 0.4,
                      ml: 0.5,
                      color: editMode ? 'primary.main' : 'text.disabled',
                      bgcolor: editMode ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      borderRadius: 1,
                      transition: 'color 0.15s, background-color 0.15s',
                      '&:hover': { color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    {editMode ? <CheckOutlined sx={{ fontSize: 14 }} /> : <TuneOutlined sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
              )}
            </ListSubheaderStyle>

            <Collapse in={isOpen} unmountOnExit>
              {group.items.filter(canSee).map(renderItem)}
            </Collapse>
          </List>
        );
      })}
    </Box>
  );
}
